import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useOrganizationChange } from './useOrganizationChange';
import { useInstitutionsStore } from '@/stores/institutions';
import { useOrganizationsStore } from '@/stores/organizations';
import { useUsersStore } from '@/stores/users';
import { TNullable } from '@/types';

/**
 * Resolves which organization (and, transitively, institution) should be
 * active right after login.
 *
 * Only super admins and organization admins get an organization switcher and
 * therefore a genuine choice to resolve. Every other role - institution admin,
 * provider, coordinator, patient, ... - gets exactly one organization: the one
 * housing their already-resolved `currentInstitution`. That organization still
 * gates institution configuration (e.g. `triage_provider`, feature toggles),
 * so it must be set for them too, even though they cannot switch it.
 * Institutions of non-admin users come without `organization_id`, so for
 * those roles it falls back to the fetched organization - a user always
 * belongs to exactly one. A user with neither organization access nor a
 * current institution still triggers this fallback.
 *
 * Priority order:
 * 1. If this bootstrap was reached via an explicit `?institutionId=`
 *    redirect (e.g. a manual organization switch) matching the already
 *    resolved institution, that institution's own organization.
 * 2. `meta.lastUtilizedOrganizationId` from the `/me` response, if it still
 *    exists among the fetched organizations.
 * 3. The organization of the already-resolved current institution.
 * 4. The alphabetically-first organization.
 *
 * Priority 1 keeps an explicit institution redirect authoritative over a
 * stale `meta.lastUtilizedOrganizationId`. Ordinary organization switches
 * persist before redirecting, so priorities 1 and 2 already agree by the
 * time this runs.
 *
 * Once a target organization is resolved, if the already-resolved
 * `currentInstitution` already belongs to it, only `currentOrganization` is
 * synced (no institution cascade) - the full `useOrganizationChange` cascade
 * always jumps to the organization's alphabetically-first institution, which
 * would otherwise undo an intentional institution switch within the same
 * organization (institution switches always do a full-page reload, so this
 * hook runs again with a freshly reset `currentOrganization` even though the
 * organization itself never changed).
 *
 * Meant to be called once from `RequiredAuthLayout`, after `fetchUserData`
 * has resolved `userData` and `currentInstitution`.
 * @returns `isResolving` — true while organizations are being fetched and
 * the initial organization/institution selection is being applied.
 */
export const useInitializeLastUtilizedOrganization = () => {
  const [isResolving, setIsResolving] = useState(true);
  const resolvedForUserId = useRef<TNullable<string>>(null);

  const [searchParams] = useSearchParams();
  const redirectInstitutionId = searchParams.get('institutionId');

  const { userData, checkIsSuperAdmin, getOrgAdminOrganizationIds } =
    useUsersStore((state) => ({
      userData: state.userData,
      checkIsSuperAdmin: state.checkIsSuperAdmin,
      getOrgAdminOrganizationIds: state.getOrgAdminOrganizationIds,
    }));
  const {
    getOrganizations,
    setCurrentOrganizationById,
    currentOrganizationId,
  } = useOrganizationsStore((state) => ({
    getOrganizations: state.getOrganizations,
    setCurrentOrganizationById: state.setCurrentOrganizationById,
    currentOrganizationId: state.currentOrganization?.id,
  }));
  const currentInstitution = useInstitutionsStore(
    (state) => state.currentInstitution,
  );
  const handleOrganizationChange = useOrganizationChange();

  useEffect(() => {
    if (!userData) return;
    if (resolvedForUserId.current === userData.id) return;
    resolvedForUserId.current = userData.id;

    const isSuperAdmin = checkIsSuperAdmin();
    const orgAdminOrganizationIds = getOrgAdminOrganizationIds();
    const hasOrganizationsAccess =
      isSuperAdmin || orgAdminOrganizationIds.length > 0;

    const resolve = async () => {
      try {
        const fetchedOrganizations = await getOrganizations();

        if (!hasOrganizationsAccess) {
          const institutionOrganizationId =
            currentInstitution?.organizationId ?? fetchedOrganizations[0]?.id;

          if (institutionOrganizationId) {
            setCurrentOrganizationById(institutionOrganizationId);
          }
          return;
        }

        const organizations = isSuperAdmin
          ? fetchedOrganizations
          : fetchedOrganizations.filter((organization) =>
              orgAdminOrganizationIds.includes(organization.id),
            );

        const lastUtilizedOrganizationId =
          userData.meta?.lastUtilizedOrganizationId;
        const isLastUtilizedOrganizationValid =
          !!lastUtilizedOrganizationId &&
          organizations.some((org) => org.id === lastUtilizedOrganizationId);

        const fallbackOrganizationId = currentInstitution?.organizationId;

        const wasExplicitlyRedirectedHere =
          !!redirectInstitutionId &&
          redirectInstitutionId === currentInstitution?.id;

        const [firstOrganization] = [...organizations].sort((a, b) =>
          a.name.localeCompare(b.name),
        );

        const targetOrganizationId = wasExplicitlyRedirectedHere
          ? fallbackOrganizationId
          : isLastUtilizedOrganizationValid
            ? lastUtilizedOrganizationId
            : currentInstitution
              ? fallbackOrganizationId
              : firstOrganization?.id;

        if (
          targetOrganizationId &&
          targetOrganizationId !== currentOrganizationId
        ) {
          // If the institution already resolved by fetchUserData already
          // belongs to the target organization, just sync currentOrganization
          // to match it - going through the full handleOrganizationChange
          // cascade would jump to the organization's first institution
          // alphabetically, undoing an intentional institution switch (e.g.
          // switching institutions within the same organization always does
          // a full-page reload, landing here with a fresh, reset
          // currentOrganization even though nothing about the organization
          // actually changed).
          const currentInstitutionOrganizationId =
            currentInstitution?.organizationId;

          if (
            currentInstitution &&
            currentInstitutionOrganizationId === targetOrganizationId
          ) {
            setCurrentOrganizationById(targetOrganizationId);
          } else {
            await handleOrganizationChange(targetOrganizationId);
          }
        }
      } catch (error) {
        console.error('Failed to resolve last utilized organization:', error);
      } finally {
        setIsResolving(false);
      }
    };

    void resolve();
  }, [
    userData,
    handleOrganizationChange,
    currentInstitution,
    redirectInstitutionId,
    getOrganizations,
    setCurrentOrganizationById,
    checkIsSuperAdmin,
    getOrgAdminOrganizationIds,
    currentOrganizationId,
  ]);

  return { isResolving };
};
